import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle, ArrowLeft, Globe } from 'lucide-react';
import { joinWaitlist } from '../services/api';

const WaitlistPage = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await joinWaitlist(email, website);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        background: '#FAFAFA',
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      {/* Header */}
      <header className="w-full py-6 px-8 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl tracking-tight" style={{ color: '#1a1a1a' }}>
          brief
        </Link>
        <Link to="/">
          <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {!submitted ? (
            <div className="text-center">
              {/* Icon */}
              <div 
                className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #E8553E 0%, #ff7b5c 100%)' }}
              >
                <span className="text-3xl">✨</span>
              </div>

              {/* Heading */}
              <h1 
                className="font-heading text-3xl sm:text-4xl mb-4"
                style={{ color: '#1a1a1a' }}
              >
                Join the <span className="italic">waitlist</span>
              </h1>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 px-4 text-base rounded-full border-gray-200 focus:border-[#E8553E] focus:ring-[#E8553E]"
                  disabled={loading}
                />
                
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    placeholder="linkedin.com/in/yourprofile (optional)"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full h-12 pl-10 pr-4 text-base rounded-full border-gray-200 focus:border-[#E8553E] focus:ring-[#E8553E]"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full text-base font-medium"
                  style={{ 
                    background: '#1a1a1a',
                    color: 'white'
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Adding you...
                    </>
                  ) : (
                    'Add me to the waitlist →'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-xs text-gray-400">
                We&apos;ll only email you about Brief. No spam, ever.
              </p>
            </div>
          ) : (
            /* Success State */
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ background: '#D1FAE5' }}
              >
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <h1 
                className="font-heading text-3xl sm:text-4xl mb-4"
                style={{ color: '#1a1a1a' }}
              >
                You&apos;re on the list!
              </h1>

              <p 
                className="text-base sm:text-lg mb-8 leading-relaxed"
                style={{ color: '#666460' }}
              >
                Thanks for your interest in Brief. We&apos;ll reach out to <strong>{email}</strong> as soon as we&apos;re ready to welcome you.
              </p>

              <Link to="/">
                <Button
                  variant="outline"
                  className="rounded-full px-8 h-12"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to home
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-sm text-gray-400">
          Made with Brief
        </p>
      </footer>
    </div>
  );
};

export default WaitlistPage;
